import React, { useRef, useState, useEffect } from "react";
import CRTShader from "./components/os/ui/CRTShader.jsx";
import DopeOS from "./components/os/DopeOS.jsx";
import { pickCRTProfile, CRT_PROFILES } from "./engine/crt/crtProfiles.js";
import { trackPageView, trackPerformance, trackError } from "./utils/analytics.js";
import { Web3Provider } from "./engine/auth/Web3Provider.jsx";


// ---------------------------------------------------------------------------
//  Global Error Boundary – captures render errors and reports via analytics
// ---------------------------------------------------------------------------

const ERROR_BOUNDARY_THROTTLE = {
  lastEventTime: 0,
  eventCount: 0,
  resetTime: 5000,
  maxEvents: 3,
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    const now = Date.now();
    if (now - ERROR_BOUNDARY_THROTTLE.lastEventTime > ERROR_BOUNDARY_THROTTLE.resetTime) {
      ERROR_BOUNDARY_THROTTLE.eventCount = 0;
    }

    ERROR_BOUNDARY_THROTTLE.eventCount++;
    ERROR_BOUNDARY_THROTTLE.lastEventTime = now;

    if (ERROR_BOUNDARY_THROTTLE.eventCount > ERROR_BOUNDARY_THROTTLE.maxEvents) {
      return { hasError: true, errorCount: 1 };
    }

    console.log('[AppErrorBoundary] 🚨 Error caught:', error.message);
    return { hasError: true, errorCount: 1 };
  }

  componentDidCatch(error, info) {
    if (ERROR_BOUNDARY_THROTTLE.eventCount <= ERROR_BOUNDARY_THROTTLE.maxEvents) {
      try {
        trackError('react_error_boundary', {
          message: error?.message,
          stack: error?.stack,
          info
        });
      } catch {/* no-op */ }
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

function determineProfileForApp() {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const forcedProfileParam = urlParams.get('profile');

  let profile = pickCRTProfile();

  if (forcedProfileParam && forcedProfileParam !== 'full') {
    const maybe = CRT_PROFILES[forcedProfileParam.toUpperCase()];
    if (maybe) profile = maybe;
  }

  if (forcedProfileParam === 'full') {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 960;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
    const aspect = vw / vh;
    const axisScale = aspect < 0.5 ? 0.5 : aspect;

    profile = {
      id: 'full_ui',
      frame: { w: vw, h: vh },
      buffer: { w: 640, h: 400 },
      contentScale: 1 / axisScale,
      phosphor: { left: 0, right: 1, top: 0, bottom: 1 },
      shaderOverrides: {
        AMBIENT: 0.20,
        GAMMA: 1.1337,
        PHOSPHOR_R: 0.3,
        PHOSPHOR_G: 1.2,
        PHOSPHOR_B: 0.3,
        VIGNETTE_STRENGTH: 0.42,
        SHADOW_STRENGTH: 0.1337,
        NOISE_AMOUNT: 0.04,
        REFLECTION_INTENSITY: 0.1337,
        SHINE: 0.4269,
        SCANLINE_INTENSITY: 0.60, // Reduced: soften the moire beat on the full UI
      },
    };
  }

  return profile;
}

function App() {
  const dopeOSRef = useRef();
  const [osState, setOsState] = useState(null);
  const [profileKey, setProfileKey] = useState(0);
  const [profile, setProfile] = useState(() => determineProfileForApp());

  useEffect(() => {
    setProfile(determineProfileForApp());
  }, [profileKey]);

  useEffect(() => {
    const handleResize = () => setProfileKey(k => k + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [crtReady, setCrtReady] = useState(false);

  useEffect(() => {
    function handleReady() {
      setCrtReady(true);
    }

    if (window.DCW_CRT_READY) handleReady();
    window.addEventListener('dcw-crt-ready', handleReady);

    const toId = setTimeout(() => {
      if (!window.DCW_CRT_READY) {
        console.warn('[App] CRT initialization timeout - forcing fallback mode');
        setCrtReady(true);
      }
    }, 15000);

    return () => {
      window.removeEventListener('dcw-crt-ready', handleReady);
      clearTimeout(toId);
    };
  }, []);

  return (
    <AppErrorBoundary>
      <Web3Provider>
        {!crtReady && (
          <img
            src="/assets/hero.png"
            alt="CRT placeholder"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              objectFit: 'contain',
              background: '#000',
              zIndex: 3,
            }}
          />
        )}



        <DopeOS
          ref={dopeOSRef}
          profile={profile}
          style={{
            visibility: 'visible',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1
          }}
          onStateChange={setOsState}
        />
        <CRTShader dopeOSCanvasRef={dopeOSRef} profile={profile} />
      </Web3Provider>
    </AppErrorBoundary>
  );
}

export default App;